/**
 * The digest as something to paste into Word: the report markdown the
 * API renders (`GET …/digest/markdown`), made into HTML the way the
 * report viewer makes it (`renderMarkdown`: parsed, then sanitised), with
 * the markdown itself as the plain-text alternative for a target that
 * takes no markup. The wording is the API's, so the paste and the report
 * say the same thing.
 */

import { renderMarkdown } from '@/components/Containers/MarkdownParser';

/** Word draws a pasted table's borders only when the markup asks for
 * them; on the page the viewer's stylesheet does that. The parser writes
 * a bare `<table>`, which gets these attributes after the sanitiser has
 * done its work (a fixed string, never the markdown's). */
const TABLE_ATTRS = 'border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse"';

/** The markdown as HTML for the clipboard. */
export function digestClipboardHtml(markdown: string): string {
  return renderMarkdown(markdown).replaceAll('<table>', `<table ${TABLE_ATTRS}>`);
}

/** Which representation reached the clipboard. */
export type DigestCopyMode = 'html' | 'text';

/**
 * Put the digest on the clipboard for Word: HTML with the markdown as the
 * plain-text alternative where the browser can take both, the markdown
 * alone where it cannot (no `ClipboardItem`, or a `write` the browser
 * refuses). Throws only when neither way works.
 */
export async function copyDigestMarkdownForWord(markdown: string): Promise<DigestCopyMode> {
  const { clipboard } = navigator;
  if (typeof ClipboardItem !== 'undefined' && typeof clipboard?.write === 'function') {
    try {
      await clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([digestClipboardHtml(markdown)], { type: 'text/html' }),
          'text/plain': new Blob([markdown], { type: 'text/plain' }),
        }),
      ]);
      return 'html';
    } catch {
      // Some browsers refuse rich writes outside a trusted gesture or
      // without a permission: the text alone is still worth having.
    }
  }
  await clipboard.writeText(markdown);
  return 'text';
}
